import { useEffect, useRef } from "react";
import { API_URL } from "../config";
import authService from "../services/auth";

/**
 * Conecta a um stream SSE de mensagens de um recurso (documento ou condicionante).
 * Reconecta automaticamente via EventSource. Fecha a conexão no unmount ou quando `enabled` vira false.
 *
 * @param {Object} params
 * @param {boolean} params.enabled - quando false, não conecta (ex.: modal fechado).
 * @param {'documentos' | 'condicionantes'} params.tipoRecurso
 * @param {string} params.recursoId
 * @param {(msg: object) => void} params.onMensagem - callback chamado a cada nova mensagem. Deve ser estável (useCallback).
 */
export default function useMensagensStream({
  enabled,
  tipoRecurso,
  recursoId,
  onMensagem,
}) {
  // Mantém o callback mais recente sem reconectar a cada render se o consumidor
  // esquecer de memoizar. A conexão só refaz quando enabled/tipoRecurso/recursoId mudam.
  const onMensagemRef = useRef(onMensagem);
  useEffect(() => {
    onMensagemRef.current = onMensagem;
  }, [onMensagem]);

  useEffect(() => {
    if (!enabled || !recursoId || !tipoRecurso) {
      return undefined;
    }

    const user = authService.getCurrentUser();
    const token = user?.token;
    if (!token) {
      return undefined;
    }

    // EventSource nativo não suporta header Authorization.
    // Backend aceita ?access_token= apenas nesta rota.
    const baseUrl = API_URL.endsWith("/")
      ? API_URL.slice(0, -1)
      : API_URL;
    const url =
      `${baseUrl}/api/${tipoRecurso}/${encodeURIComponent(recursoId)}` +
      `/mensagens/stream?access_token=${encodeURIComponent(token)}`;

    let eventSource;
    try {
      eventSource = new EventSource(url);
    } catch (err) {
      return undefined;
    }

    const handleMensagem = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMensagemRef.current?.(data);
      } catch (err) {
        // Ignora payload inválido — não derrubar a conexão.
      }
    };

    eventSource.addEventListener("mensagem", handleMensagem);

    // EventSource reconecta automaticamente em erro de rede.
    // Não precisamos de handler além do default; logar opcionalmente em dev.
    eventSource.onerror = () => {};

    return () => {
      eventSource.removeEventListener("mensagem", handleMensagem);
      eventSource.close();
    };
  }, [enabled, tipoRecurso, recursoId]);
}
